import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { getXAxisDomainForHistoricGraph, minMaxAxisDomain } from '@/components/Charts/utils';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useMembershipType } from '@/hooks/useMembershipType';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';
import { printYear } from '@/utils/formatting/date';

import { NoData } from '../../../components/NoData';
import { Tile, TileCapsuleContent, TileCta, TileTitle } from './Tile';

export const BiodiversityTile = () => {
  const { t } = useTranslation();
  const pathSet =
    useMembershipType() === MembershipWithOrganizationTypeEnum.land_steward ? paths.landSteward : paths.buyer;

  const plotGroupId = usePlotGroupId();
  const { pathname, search } = useLocation();
  const currentPath = `${pathname}${search}`;

  const biodiversityZonePercentHistoricGraphDataFact = IndicatorName.biodiversity_buffer_zone_pct_historic_graph;
  const { [biodiversityZonePercentHistoricGraphDataFact]: biodiversityZonePercentHistoricGraphData } =
    useOrganizationIndicatorGraphFact<BiodiversityZonePercentHistoricGraphData[]>(
      biodiversityZonePercentHistoricGraphDataFact,
    );
  const biodiversityZonePercentHistoricGraphDataFormatted = biodiversityZonePercentHistoricGraphData?.value
    ?.filter((d) => d.name === 'biodiversity_percent')
    // @ts-ignore typescript does not allow arithmetic operations between date objects
    .sort((pointA, pointB) => new Date(pointA.date) - new Date(pointB.date))
    .map((d) => ({ ...d, date: new Date(d.date).getTime() }));

  const biodiversityLatestAnalysisFact = IndicatorName.biodiversity_buffer_zone_pct;
  const { [biodiversityLatestAnalysisFact]: biodiversityLatestAnalysis } =
    useOrganizationIndicatorReportingFact<number>(biodiversityLatestAnalysisFact);

  const chartData = biodiversityZonePercentHistoricGraphDataFormatted;

  const unit = t(`global.ui.unit.%`);
  let biodiversityLatestAnalysisDisplay = `${useDisplayNumber(biodiversityLatestAnalysis?.value ?? 0)} ${unit}`;
  biodiversityLatestAnalysisDisplay = !isNullish(biodiversityLatestAnalysis?.value)
    ? biodiversityLatestAnalysisDisplay
    : '-';

  const noChartData = !chartData?.length;

  if (noChartData) {
    return <NoData />;
  }

  return (
    <Tile
      chart={<Chart chartData={chartData} />}
      title={<TileTitle>{t('global.analysis.biodiversity')}</TileTitle>}
      capsuleContent={<TileCapsuleContent>{biodiversityLatestAnalysisDisplay}</TileCapsuleContent>}
      cta={
        <TileCta
          to={buildPath(pathSet.biodiversityDetails, { queryParams: { plotGroupId } })}
          state={{ from: currentPath }}
        >
          {t('global.analysis.viewDetails')}
        </TileCta>
      }
    />
  );
};

type BiodiversityZonePercentHistoricGraphData = {
  date: number;
  name: 'biodiversity_percent';
  value: number;
  unit: '%';
};

const Chart = ({ chartData }: { chartData: BiodiversityZonePercentHistoricGraphData[] }) => {
  const firstTick = chartData.at(0)?.date;
  const lastTick = chartData.at(-1)?.date;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const xTicksDomain = getXAxisDomainForHistoricGraph(firstTick!, lastTick!) ?? [
    new Date().getFullYear(),
    new Date().getFullYear(),
  ];
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const startYear = xTicksDomain[0]!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const endYear = xTicksDomain[1]!;
  const ticksRange = Array(endYear - startYear + 1).keys();
  const xTicks = Array.from(ticksRange).map((x) => new Date(`${startYear + x}-12-31`).getTime());
  const yearsSubset = xTicks.filter((_, i) => i % 2 !== 0 && i !== xTicks.length - 1);

  return (
    <ResponsiveContainer width='100%' height={80}>
      <AreaChart
        data={chartData}
        margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
        }}
      >
        <defs>
          <linearGradient id='biodiversityZone' x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor='#558707' stopOpacity={0.3} />
            <stop offset='100%' stopColor='#558707' stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          isAnimationActive={false}
          type='monotone'
          dataKey='value'
          stroke='#92B55F'
          strokeWidth={2}
          fill='url(#biodiversityZone)'
          fillOpacity={1}
        />
        <XAxis
          dataKey='date'
          scale='time'
          type='number'
          domain={['dataMin', 'dataMax']}
          ticks={yearsSubset}
          tickFormatter={printYear}
          axisLine={false}
          tickLine={false}
        />
        <YAxis hide dataKey='value' type='number' scale='sequential' width={0} domain={minMaxAxisDomain(0, 1.2)} />
      </AreaChart>
    </ResponsiveContainer>
  );
};
